import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getMyPokemon } from "../lib/api";

const MAX_POKEMON = 1025;
const SPRITE_BASE = "https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon";

function spriteUrl(id, shiny) {
  return shiny ? `${SPRITE_BASE}/shiny/${id}.png` : `${SPRITE_BASE}/${id}.png`;
}

export default function Pokedex() {
  const navigate = useNavigate();
  const [caught, setCaught] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // "all" | "caught" | "missing" | "shiny"

  useEffect(() => {
    (async () => {
      try {
        const list = await getMyPokemon();
        setCaught(list);
      } catch {
        setCaught([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const caughtMap = useMemo(() => {
    const map = new Map();
    for (const p of caught) {
      const existing = map.get(p.pokemonId);
      if (!existing) {
        map.set(p.pokemonId, { normal: !p.pokemonIsShiny, shiny: !!p.pokemonIsShiny, count: 1 });
      } else {
        existing.count += 1;
        if (p.pokemonIsShiny) existing.shiny = true;
        else existing.normal = true;
      }
    }
    return map;
  }, [caught]);

  const totalCaught = caughtMap.size;
  const totalShiny = [...caughtMap.values()].filter(v => v.shiny).length;

  const entries = useMemo(() => {
    const list = [];
    for (let id = 1; id <= MAX_POKEMON; id++) {
      const entry = caughtMap.get(id);
      const isCaught = !!entry;
      const isShiny = entry?.shiny || false;

      if (filter === "caught" && !isCaught) continue;
      if (filter === "missing" && isCaught) continue;
      if (filter === "shiny" && !isShiny) continue;

      list.push({ id, isCaught, isShiny, hasNormal: entry?.normal || false, count: entry?.count || 0 });
    }
    return list;
  }, [caughtMap, filter]);

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <div className="px-6 pt-6 pb-3 flex items-center justify-between">
        <h1 className="text-3xl font-mono">pokédex</h1>
        <button
          className="rounded-full border px-4 py-2 text-sm"
          onClick={() => navigate("/")}
        >
          Volver
        </button>
      </div>

      <div className="px-6 pb-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-neutral-600">
            {totalCaught}/{MAX_POKEMON} capturados
            {totalShiny > 0 ? ` · ${totalShiny} shiny` : ""}
          </div>
          <div className="text-xs text-neutral-400">
            {((totalCaught / MAX_POKEMON) * 100).toFixed(1)}%
          </div>
        </div>
        <div className="mt-2 w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-black rounded-full transition-all duration-500"
            style={{ width: `${(totalCaught / MAX_POKEMON) * 100}%` }}
          />
        </div>
      </div>

      <div className="px-6 pb-3">
        <div className="inline-flex items-center rounded-full border overflow-hidden text-xs">
          {[
            { key: "all", label: "Todos" },
            { key: "caught", label: "Capturados" },
            { key: "missing", label: "Faltan" },
            { key: "shiny", label: "Shiny" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 ${filter === f.key ? "bg-black text-white" : "bg-transparent"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 px-6 pb-6 overflow-y-auto">
        {loading ? (
          <div className="text-neutral-500 text-center mt-8">Cargando...</div>
        ) : (
          <div className="grid grid-cols-5 gap-1.5">
            {entries.map((e) => (
              <PokemonCell key={`${e.id}-${filter}`} entry={e} filter={filter} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PokemonCell({ entry, filter }) {
  const { id, isCaught, isShiny, count } = entry;
  const showShiny = filter === "shiny" && isShiny;
  const src = spriteUrl(id, showShiny);

  return (
    <div
      className={`relative aspect-square rounded-xl border flex flex-col items-center justify-center ${
        isCaught
          ? isShiny
            ? "border-yellow-300 bg-yellow-50"
            : "border-neutral-200 bg-white"
          : "border-neutral-100 bg-neutral-50"
      }`}
    >
      {isCaught ? (
        <img
          src={src}
          alt={`#${id}`}
          className="w-10 h-10 object-contain"
          loading="lazy"
        />
      ) : (
        <div className="w-10 h-10 flex items-center justify-center">
          <span className="text-neutral-300 text-lg">?</span>
        </div>
      )}
      <div className={`text-[9px] font-mono leading-none mt-0.5 ${isCaught ? "text-neutral-600" : "text-neutral-300"}`}>
        #{String(id).padStart(3, "0")}
      </div>
      {count > 1 ? (
        <span className="absolute top-0.5 left-1 text-[8px] font-mono text-neutral-400">
          x{count}
        </span>
      ) : null}
      {isShiny && filter !== "shiny" ? (
        <span className="absolute top-0.5 right-0.5 text-[8px]" title="shiny">
          &#x2728;
        </span>
      ) : null}
    </div>
  );
}
